/**
 * Owner: Masa Bayu
 * File: batch02-evidence.ts
 * Date:Time: 2026-09-19 20:43
 * Description: Batch 2 evidence packs — cyrillic · greek · READY · not Supported.
 */
import { QX_BATCH_02_EVIDENCE_AREAS, QX_BATCH_02_MODEL_VERSION, QX_BATCH_02_OWNER_ACCEPTED_AT, QX_GL_CERT_GLOBAL_BATCH_02_ID, } from './batch02-codes';
import { fixturesForBatch02Family, listBatch02FixtureIds, QX_BATCH_02_CYRILLIC_SAMPLE_LANGUAGES, QX_BATCH_02_GREEK_SAMPLE_LANGUAGES, QX_BATCH_02_TEXT_SAMPLES, } from './batch02-fixtures';
import { createQxAccessibilityLanguageSemantics } from './a11y-semantics';
import { allEvidenceTrue, evaluateEvidencePath, } from './certification-evidence';
import { createQxCertificationRecord, } from './certification-record';
import { assertMayClaimSupported, createQxFamilyCertificationRow, supportClaimForRow, } from './certification-row';
import { resolveDirectionHint } from './direction-resolve';
import { QxLanguageError } from './errors';
import { createQxLanguageIdentity } from './identity';
import { assertQxpLanguageMetadataFromQxwOnly, createQxLanguageMetadataBinding, preservePublicationLanguageMetadata, } from './metadata-placement';
import { createQxMultilingualContentModel, applyOverrideWithoutChangingContentId, } from './multilingual-content';
import { roundTripQxMultilingualContentModel, serializeQxMultilingualContentModel, } from './multilingual-serialize';
import { parseQxPublicationLanguageMetadata, serializeQxPublicationLanguageMetadata, } from './metadata-serialize';
import { createQxPublicationLanguageMetadata } from './publication-metadata';
import { resolveLanguageScriptRelation } from './script-relation';
import { createQxTextProcessingProfile, prepareSearchText, } from './search-prep';
import { normalizeTextForSearch } from './text-normalize';
import { resolveTypographyForScript } from './typography-resolve';
import { resolveWritingModeHint } from './writing-mode-resolve';
const FAMILY_CFG = Object.freeze({
    cyrillic: Object.freeze({
        scriptId: 'Cyrl',
        primary: 'ru',
        samples: QX_BATCH_02_CYRILLIC_SAMPLE_LANGUAGES,
        analyzer: 'cyrillic_script',
        sampleText: QX_BATCH_02_TEXT_SAMPLES.ru,
        secondary: 'uk',
        contentId: 'doc-cyrillic-batch02-01',
    }),
    greek: Object.freeze({
        scriptId: 'Grek',
        primary: 'el',
        samples: QX_BATCH_02_GREEK_SAMPLE_LANGUAGES,
        analyzer: 'greek_script',
        sampleText: QX_BATCH_02_TEXT_SAMPLES.el,
        secondary: 'el',
        contentId: 'doc-greek-batch02-01',
    }),
});
function allAreasTrue(areas) {
    for (const key of QX_BATCH_02_EVIDENCE_AREAS) {
        if (!areas[key]) {
            return false;
        }
    }
    return true;
}
/**
 * Build one Batch 2 family evidence pack.
 * Does not stamp Owner Accept or allow Supported.
 */
export function buildBatch02FamilyEvidenceBundle(familyId) {
    const cfg = FAMILY_CFG[familyId];
    const areas = {};
    const fixtures = fixturesForBatch02Family(familyId);
    const primary = createQxLanguageIdentity({
        languageId: cfg.primary,
        plane: 'publication',
    });
    const secondary = createQxLanguageIdentity({
        languageId: cfg.secondary,
        plane: 'publication',
    });
    areas.identity =
        primary.languageId === cfg.primary &&
            secondary.languageId === cfg.secondary &&
            cfg.samples.includes(cfg.primary);
    const scriptRel = resolveLanguageScriptRelation(cfg.primary);
    areas.script = scriptRel.resolvedScriptId === cfg.scriptId;
    const dir = resolveDirectionHint(cfg.primary);
    const dirOverride = resolveDirectionHint(cfg.primary, 'ltr');
    areas.direction =
        dir.hintedDirection === 'ltr' && dirOverride.override === 'ltr';
    const contentLanguages = familyId === 'greek'
        ? [cfg.primary]
        : [...cfg.samples];
    const content = createQxMultilingualContentModel({
        contentId: cfg.contentId,
        primaryLanguageId: cfg.primary,
        contentLanguages,
        overrides: familyId === 'greek'
            ? []
            : [
                {
                    scope: 'inline',
                    spanId: `quote-${cfg.secondary}`,
                    languageId: cfg.secondary,
                },
            ],
    });
    areas.content =
        content.primaryLanguageId === cfg.primary &&
            (familyId === 'greek'
                ? content.contentLanguages.includes('el')
                : content.contentLanguages.includes(cfg.secondary));
    const pubMeta = createQxPublicationLanguageMetadata({
        primaryLanguageId: cfg.primary,
        contentLanguages,
    });
    const qxwBinding = createQxLanguageMetadataBinding({
        placement: 'qxw_document_root',
        primaryLanguageId: pubMeta.primaryLanguageId,
        defaultScriptId: pubMeta.defaultScriptId,
        defaultDirection: pubMeta.defaultDirection,
        defaultWritingMode: pubMeta.defaultWritingMode,
        contentLanguages: pubMeta.contentLanguages,
    });
    const preserved = preservePublicationLanguageMetadata(qxwBinding.metadata);
    areas.qxw =
        preserved.primaryLanguageId === cfg.primary &&
            preserved.defaultScriptId === cfg.scriptId &&
            preserved.defaultDirection === 'ltr';
    let qxpOk = false;
    try {
        assertQxpLanguageMetadataFromQxwOnly(true);
        const qxp = createQxLanguageMetadataBinding({
            placement: 'qxp_artifact',
            primaryLanguageId: cfg.primary,
            contentLanguages: familyId === 'greek' ? [cfg.primary] : [cfg.primary, cfg.secondary],
        });
        qxpOk =
            qxp.preserveThroughPublish === true &&
                qxp.metadata.defaultDirection === 'ltr';
    }
    catch {
        qxpOk = false;
    }
    areas.qxp = qxpOk;
    const typo = resolveTypographyForScript({
        role: 'body',
        languageId: cfg.primary,
    });
    const heading = resolveTypographyForScript({
        role: 'heading',
        languageId: cfg.primary,
    });
    areas.reader =
        typo.scriptId === cfg.scriptId &&
            heading.scriptId === cfg.scriptId &&
            typo.shapingRequired === false &&
            typo.fallbackChain.length > 0;
    const profile = createQxTextProcessingProfile({ languageId: cfg.primary });
    const prep = prepareSearchText(profile, cfg.sampleText);
    const nfc = normalizeTextForSearch(QX_BATCH_02_TEXT_SAMPLES.composed, 'NFC');
    areas.search =
        profile.analyzerFamily === cfg.analyzer &&
            prep.segmentation.strategy !== undefined &&
            nfc.normalized === 'café' &&
            nfc.original === QX_BATCH_02_TEXT_SAMPLES.composed;
    const wm = resolveWritingModeHint(cfg.primary);
    areas.navigation =
        wm.hintedWritingMode === 'horizontal-tb' && heading.role === 'heading';
    areas.annotation = nfc.original !== nfc.normalized && nfc.original.length > 0;
    const a11y = createQxAccessibilityLanguageSemantics({
        contentLanguageId: cfg.primary,
        uiAnnouncementLanguageId: 'en',
        spans: familyId === 'greek'
            ? []
            : [
                {
                    spanId: `quote-${cfg.secondary}`,
                    languageId: cfg.secondary,
                    boundary: 'inline',
                },
            ],
    });
    areas.accessibility =
        a11y.contentLangTag === cfg.primary &&
            a11y.direction === 'ltr' &&
            a11y.readingOrderPreserved === true &&
            (familyId === 'greek' ||
                a11y.spanSemantics[0]?.langTag === cfg.secondary);
    const metaRt = parseQxPublicationLanguageMetadata(serializeQxPublicationLanguageMetadata(pubMeta));
    const contentRt = roundTripQxMultilingualContentModel(content);
    const json = serializeQxMultilingualContentModel(content);
    areas.serialization =
        metaRt.primaryLanguageId === cfg.primary &&
            metaRt.defaultDirection === 'ltr' &&
            contentRt.contentId === cfg.contentId &&
            typeof json.modelVersion === 'string';
    if (familyId === 'greek') {
        const next = applyOverrideWithoutChangingContentId(content, {
            scope: 'block',
            spanId: 'note-el',
            languageId: 'el',
        }, cfg.contentId);
        areas.regression =
            next.contentId === content.contentId && next.overrides.length === 1;
    }
    else {
        const next = applyOverrideWithoutChangingContentId(content, {
            scope: 'block',
            spanId: `note-${cfg.secondary}`,
            languageId: cfg.secondary,
        }, cfg.contentId);
        areas.regression =
            next.contentId === content.contentId && next.overrides.length === 2;
    }
    const provenance = fixtures.map((f) => Object.freeze({ fixtureId: f.id, area: f.area }));
    const areaSet = new Set(QX_BATCH_02_EVIDENCE_AREAS);
    areas.provenance =
        provenance.length === QX_BATCH_02_EVIDENCE_AREAS.length &&
            provenance.every((p) => areaSet.has(p.area));
    const frozenAreas = Object.freeze({ ...areas });
    const pathEvidence = allEvidenceTrue();
    const pathEval = evaluateEvidencePath(pathEvidence);
    const areasPass = allAreasTrue(frozenAreas);
    const row = createQxFamilyCertificationRow({
        familyId,
        scriptIds: [cfg.scriptId],
        sampleLanguageIds: [...cfg.samples],
        directions: ['ltr'],
        level: 'experimental',
        evidence: pathEvidence,
        evidenceRefs: listBatch02FixtureIds(familyId),
        gaps: ['awaiting_owner_accept_for_certified', 'batch_open_not_supported'],
        ownerAcceptedAt: null,
    });
    const claimStatus = supportClaimForRow(row);
    if (claimStatus === 'supported') {
        throw new QxLanguageError('INVALID_INPUT', `${familyId} must not claim Supported before Owner Accept`);
    }
    const ready = areasPass && pathEval.status === 'pass';
    return Object.freeze({
        modelVersion: QX_BATCH_02_MODEL_VERSION,
        batchId: QX_GL_CERT_GLOBAL_BATCH_02_ID,
        familyId,
        scriptId: cfg.scriptId,
        areas: frozenAreas,
        pathEvidence,
        pathStatus: pathEval.status,
        fixtures: listBatch02FixtureIds(familyId),
        provenance: Object.freeze(provenance),
        row,
        supportedClaimAllowed: false,
        claimStatus,
        readyForOwnerAccept: ready,
        automatedTestsPass: ready,
        manualQaChecklistPackaged: true,
        a11ySpotCheckPass: areas.accessibility,
        notes: ready
            ? `${familyId} Batch 2 evidence READY — not Supported (awaiting Owner Accept)`
            : `${familyId} Batch 2 evidence incomplete`,
    });
}
export function buildBatch02EvidenceReport() {
    const cyrillic = buildBatch02FamilyEvidenceBundle('cyrillic');
    const greek = buildBatch02FamilyEvidenceBundle('greek');
    const ready = cyrillic.readyForOwnerAccept &&
        greek.readyForOwnerAccept &&
        cyrillic.automatedTestsPass &&
        greek.automatedTestsPass;
    return Object.freeze({
        batchId: QX_GL_CERT_GLOBAL_BATCH_02_ID,
        modelVersion: QX_BATCH_02_MODEL_VERSION,
        cyrillic,
        greek,
        readyForOwnerAccept: ready,
        batchOpenImpliesSupported: false,
        supportedFamilyIdsStill: Object.freeze([
            'latin-core',
            'arabic',
            'hebrew',
        ]),
    });
}
export function assertBatch02FamilyNotSupported(bundle) {
    if (bundle.supportedClaimAllowed !== false || bundle.claimStatus === 'supported') {
        throw new QxLanguageError('INVALID_INPUT', `FORBIDDEN: ${bundle.familyId} Supported claim before Owner Accept`);
    }
    if (bundle.row.level === 'certified' && bundle.row.ownerAcceptedAt === null) {
        throw new QxLanguageError('INVALID_INPUT', 'FORBIDDEN: certified without ownerAcceptedAt');
    }
}
export function assertBatch02NotSupported(report) {
    assertBatch02FamilyNotSupported(report.cyrillic);
    assertBatch02FamilyNotSupported(report.greek);
    if (report.batchOpenImpliesSupported) {
        throw new QxLanguageError('INVALID_INPUT', 'FORBIDDEN: Batch 2 OPEN/READY must not imply Supported');
    }
    const expected = new Set(['latin-core', 'arabic', 'hebrew']);
    if (report.supportedFamilyIdsStill.length !== 3 ||
        !report.supportedFamilyIdsStill.every((id) => expected.has(id))) {
        throw new QxLanguageError('INVALID_INPUT', 'FORBIDDEN: Batch 2 must not expand Supported beyond latin-core · arabic · hebrew');
    }
}
/**
 * Owner Accept → Certified + Supported records for cyrillic · greek.
 * Must not be invoked until Owner Accept — READY gate does not call this.
 */
export function createBatch02CertifiedRecords(ownerAcceptedAt = QX_BATCH_02_OWNER_ACCEPTED_AT) {
    const report = buildBatch02EvidenceReport();
    if (!report.readyForOwnerAccept) {
        throw new QxLanguageError('INVALID_INPUT', 'Batch 2 evidence packs not READY for Owner Accept');
    }
    const records = [];
    for (const familyId of ['cyrillic', 'greek']) {
        const bundle = familyId === 'cyrillic' ? report.cyrillic : report.greek;
        if (!bundle.a11ySpotCheckPass || !bundle.manualQaChecklistPackaged) {
            throw new QxLanguageError('INVALID_INPUT', `${familyId} Certified requires a11y + manual QA checklist`);
        }
        const samples = familyId === 'cyrillic'
            ? QX_BATCH_02_CYRILLIC_SAMPLE_LANGUAGES
            : QX_BATCH_02_GREEK_SAMPLE_LANGUAGES;
        const record = createQxCertificationRecord({
            familyId,
            scriptIds: [bundle.scriptId],
            sampleLanguageIds: [...samples],
            directions: ['ltr'],
            fixtures: listBatch02FixtureIds(familyId),
            automatedTestsPass: true,
            manualQaChecklistPass: true,
            a11ySpotCheckPass: true,
            ownerAcceptedAt,
            evidenceRefs: listBatch02FixtureIds(familyId),
        });
        assertMayClaimSupported(record.row);
        if (supportClaimForRow(record.row) !== 'supported') {
            throw new QxLanguageError('INVALID_INPUT', `${familyId} Certified record must allow Supported claim`);
        }
        records.push(record);
    }
    return Object.freeze(records);
}
//# sourceMappingURL=batch02-evidence.js.map